/**
 * CycleClosingCarousel
 *
 * The homepage's arrival state after a cycle closes: a short swipeable section
 * that registers the ending before Home goes back to being Home.
 *
 * It exists because closing a cycle used to land the patient on an unchanged
 * homepage with no acknowledgement that anything had happened. Slide one names
 * the ending, slide two says what is true now, and slide three — only for endings
 * that actually have a next step — points onward. A cancellation or an unresolved
 * result stops at two rather than manufacturing a decision the patient has not
 * been offered.
 *
 * Tone comes from the ending, never from this surface: `mayCelebrate` is true
 * only for a recorded pregnancy, so no change here can celebrate a loss.
 *
 * Dismissing is an acknowledgement, not a hide: the caller persists it against
 * the outcome, so it stays dismissed across devices, and the ending remains
 * reachable through the cycle's own history rather than only from here.
 */

import { useRef, useState } from "react";
import {
  Pressable,
  ScrollView,
  View,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { CompactActionButton } from "@louise/moon-ui";
import { ConfettiBurst } from "@louise/moon-ui/primitives/ConfettiBurst";
import { GLASS_CARD_STYLE, EMBER } from "@louise/moon-ui/theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useTranslations } from "../../i18n";
import type { CycleClosingContent } from "./cycleClosingSlides";

const NS = "homepage.cycle_closing.ui";

export function CycleClosingCarousel({
  content,
  storedCount,
  onDone,
  onOpenCycle,
  isSaving = false,
  canAcknowledge = true,
}: {
  content: CycleClosingContent;
  /**
   * Real stored-specimen count for a preservation or donation ending, or null
   * when the lab numbers are not in yet. Never defaulted to zero — an absent
   * record is not a count of nothing.
   */
  storedCount: number | null;
  /** Acknowledge the ending; Home stops greeting it from then on. */
  onDone: () => void;
  /** Open the cycle this ending belongs to. */
  onOpenCycle: () => void;
  isSaving?: boolean;
  /**
   * Whether this viewer may acknowledge the ending.
   *
   * False for a partner. `acknowledgedAt` lives on the shared outcome, so a
   * partner tapping it would retire the greeting for the patient too — deciding
   * on their behalf that they have seen their own result. The partner still
   * reads the ending and can open the cycle; it simply stays until the patient
   * acknowledges it.
   */
  canAcknowledge?: boolean;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CycleClosingCarousel");
  const [index, setIndex] = useState(0);
  const [width, setWidth] = useState(0);
  const confettiTrigger = useRef(content.mayCelebrate ? 1 : 0).current;

  const slides = content.slides;
  const isLast = index >= slides.length - 1;

  const onLayout = (event: LayoutChangeEvent) => {
    setWidth(event.nativeEvent.layout.width);
  };

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (width <= 0) return;
    const next = Math.round(event.nativeEvent.contentOffset.x / width);
    if (next !== index) setIndex(next);
  };

  return (
    <View
      testID="cycle-closing-carousel"
      style={{ marginTop: 16, position: "relative" }}
    >
      {content.mayCelebrate ? (
        <ConfettiBurst trigger={confettiTrigger} />
      ) : null}

      <View
        style={{
          ...(GLASS_CARD_STYLE as Record<string, unknown>),
          borderRadius: 18,
          paddingVertical: 18,
          gap: 14,
          overflow: "hidden",
        }}
      >
        <View onLayout={onLayout}>
          <ScrollView
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            onScroll={onScroll}
            scrollEventThrottle={16}
          >
            {slides.map((slide, slideIndex) => (
              <View
                key={slide.titleKey}
                style={{
                  width: width || undefined,
                  paddingHorizontal: 18,
                  gap: 8,
                }}
              >
                <Text
                  accessibilityRole={slideIndex === 0 ? "header" : undefined}
                  style={{
                    fontSize: 17,
                    lineHeight: 23,
                    color: EMBER.colors.text,
                    fontWeight: "700",
                  }}
                >
                  {t(slide.titleKey)}
                </Text>
                <Text
                  style={{
                    fontSize: 13,
                    lineHeight: 20,
                    color: EMBER.colors.textMid,
                    fontWeight: "400",
                  }}
                >
                  {t(slide.bodyKey, {
                    // Only ever interpolated where the copy asks for it, and
                    // only when a real count exists.
                    params: {
                      count: storedCount != null ? `${storedCount}` : "",
                    },
                  })}
                </Text>
              </View>
            ))}
          </ScrollView>
        </View>

        {/* Position, then the way out. Dots are not pressable: this is a short
            read, and a tappable dot competes with the swipe for the same pixels. */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          {slides.map((slide, dotIndex) => (
            <View
              key={`dot-${slide.titleKey}`}
              style={{
                width: dotIndex === index ? 16 : 6,
                height: 6,
                borderRadius: 3,
                backgroundColor:
                  dotIndex === index
                    ? EMBER.colors.text
                    : "rgba(255, 255, 255, 0.5)",
              }}
            />
          ))}
        </View>

        <View style={{ paddingHorizontal: 18, gap: 10 }}>
          {canAcknowledge ? (
            <CompactActionButton
              testID="cycle-closing-done"
              label={t(isLast ? `${NS}.done` : `${NS}.done_early`)}
              onPress={onDone}
              loading={isSaving}
              numberOfLines={2}
            />
          ) : null}
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t(`${NS}.open_cycle`)),
                `${NS}.open_cycle`,
              );
              onOpenCycle();
            }}
            accessibilityRole="button"
            accessibilityLabel={t(`${NS}.open_cycle`)}
            style={({ pressed }) => ({
              alignSelf: "center",
              paddingVertical: 6,
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <Text
              style={{
                fontSize: 12,
                lineHeight: 17,
                color: EMBER.colors.textMid,
                fontWeight: "700",
              }}
            >
              {t(`${NS}.open_cycle`)}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}
